/**
 * CollectionStatsPanel Component
 *
 * Displays comprehensive collection statistics including metadata,
 * floor price, volume stats, NFT processing status breakdown, and last refresh timestamp.
 */

import React, { useState } from 'react';
import type { CollectionDetailResponse, RarityMethodology, ValuationStrategy } from '../../types/admin';
import {
  RarityMethodologySelect,
  getMethodologyLabel,
  getMethodologyDescription,
} from './RarityMethodologySelect';
import {
  ValuationStrategySelect,
  getStrategyLabel,
  getStrategyDescription,
} from './ValuationStrategySelect';
import { updateRarityMethodology, updateValuationStrategy, triggerValuationRecalculation, updateVoteboardId, updateRuggedStatus } from '../../services/adminApi';

interface CollectionStatsPanelProps {
  collection: CollectionDetailResponse['collection'];
  nftStats: CollectionDetailResponse['nftStats'];
  onMethodologyUpdated?: () => void;
  onValuationStrategyUpdated?: () => void;
}

export function CollectionStatsPanel({ collection, nftStats, onMethodologyUpdated, onValuationStrategyUpdated }: CollectionStatsPanelProps) {
  const [isUpdatingMethodology, setIsUpdatingMethodology] = useState(false);
  const [methodologyError, setMethodologyError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingMethodology, setPendingMethodology] = useState<RarityMethodology | null>(null);

  // Valuation strategy state
  const [isUpdatingStrategy, setIsUpdatingStrategy] = useState(false);
  const [strategyError, setStrategyError] = useState<string | null>(null);
  const [showStrategyConfirmDialog, setShowStrategyConfirmDialog] = useState(false);
  const [pendingStrategy, setPendingStrategy] = useState<ValuationStrategy | null>(null);

  // Valuation recalculation trigger state
  const [isTriggeringValuation, setIsTriggeringValuation] = useState(false);
  const [valuationTriggerSuccess, setValuationTriggerSuccess] = useState<string | null>(null);
  const [valuationTriggerError, setValuationTriggerError] = useState<string | null>(null);

  // VoteBoard ID state
  const [isEditingVoteboardId, setIsEditingVoteboardId] = useState(false);
  const [voteboardIdInput, setVoteboardIdInput] = useState('');
  const [isUpdatingVoteboardId, setIsUpdatingVoteboardId] = useState(false);
  const [voteboardError, setVoteboardError] = useState<string | null>(null);
  const [voteboardSuccess, setVoteboardSuccess] = useState<string | null>(null);

  // Rugged status state
  const [isUpdatingRugged, setIsUpdatingRugged] = useState(false);
  const [ruggedError, setRuggedError] = useState<string | null>(null);
  const [ruggedSuccess, setRuggedSuccess] = useState<string | null>(null);

  // Handle null collection case (whitelisted but not yet indexed)
  if (!collection) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-500">
          <p className="text-lg font-medium">Collection Not Yet Indexed</p>
          <p className="text-sm mt-2">
            This issuer/taxon is whitelisted but no NFTs have been indexed yet.
          </p>
        </div>
      </div>
    );
  }

  const formatXRP = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return `${amount.toLocaleString()} XRP`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'metadata_fetched':
        return 'Metadata Fetched';
      case 'processing_media':
        return 'Processing Media';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-700';
      case 'metadata_fetched':
        return 'bg-blue-100 text-blue-700';
      case 'processing_media':
        return 'bg-purple-100 text-purple-700';
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'failed':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Handler for when methodology dropdown changes
  const handleMethodologyChange = (newMethodology: RarityMethodology) => {
    if (newMethodology === collection.rarityMethodology) return;
    setPendingMethodology(newMethodology);
    setShowConfirmDialog(true);
    setMethodologyError(null);
  };

  // Confirm methodology change
  const confirmMethodologyChange = async () => {
    if (!pendingMethodology) return;

    setIsUpdatingMethodology(true);
    setMethodologyError(null);

    try {
      await updateRarityMethodology(
        collection.creatorAddress,
        collection.taxon,
        pendingMethodology,
        true // trigger recalculation
      );
      setShowConfirmDialog(false);
      setPendingMethodology(null);
      onMethodologyUpdated?.();
    } catch (error: any) {
      setMethodologyError(error.message || 'Failed to update methodology');
    } finally {
      setIsUpdatingMethodology(false);
    }
  };

  // Cancel methodology change
  const cancelMethodologyChange = () => {
    setShowConfirmDialog(false);
    setPendingMethodology(null);
  };

  // Handler for when valuation strategy dropdown changes
  const handleStrategyChange = (newStrategy: ValuationStrategy) => {
    if (newStrategy === collection.valuationStrategy) return;
    setPendingStrategy(newStrategy);
    setShowStrategyConfirmDialog(true);
    setStrategyError(null);
  };

  // Confirm valuation strategy change
  const confirmStrategyChange = async () => {
    if (!pendingStrategy) return;

    setIsUpdatingStrategy(true);
    setStrategyError(null);

    try {
      await updateValuationStrategy(
        collection.creatorAddress,
        collection.taxon,
        pendingStrategy,
        true // trigger recalculation
      );
      setShowStrategyConfirmDialog(false);
      setPendingStrategy(null);
      onValuationStrategyUpdated?.();
    } catch (error: any) {
      setStrategyError(error.message || 'Failed to update valuation strategy');
    } finally {
      setIsUpdatingStrategy(false);
    }
  };

  // Cancel valuation strategy change
  const cancelStrategyChange = () => {
    setShowStrategyConfirmDialog(false);
    setPendingStrategy(null);
  };

  // Trigger valuation recalculation without changing strategy
  const handleTriggerValuation = async () => {
    setIsTriggeringValuation(true);
    setValuationTriggerError(null);
    setValuationTriggerSuccess(null);

    try {
      const result = await triggerValuationRecalculation(
        collection.creatorAddress,
        collection.taxon
      );
      setValuationTriggerSuccess(result.note);
      // Clear success message after 5 seconds
      setTimeout(() => setValuationTriggerSuccess(null), 5000);
    } catch (error: any) {
      setValuationTriggerError(error.message || 'Failed to trigger valuation recalculation');
    } finally {
      setIsTriggeringValuation(false);
    }
  };

  // Start editing VoteBoard ID
  const handleEditVoteboardId = () => {
    setVoteboardIdInput(collection.xrplVoteboardId?.toString() || '');
    setIsEditingVoteboardId(true);
    setVoteboardError(null);
    setVoteboardSuccess(null);
  };

  // Cancel editing VoteBoard ID
  const handleCancelVoteboardEdit = () => {
    setIsEditingVoteboardId(false);
    setVoteboardIdInput('');
    setVoteboardError(null);
  };

  // Save VoteBoard ID
  const handleSaveVoteboardId = async () => {
    setIsUpdatingVoteboardId(true);
    setVoteboardError(null);
    setVoteboardSuccess(null);

    try {
      const newId = voteboardIdInput.trim()
        ? parseInt(voteboardIdInput.trim(), 10)
        : null;

      if (newId !== null && (isNaN(newId) || newId < 1)) {
        throw new Error('VoteBoard ID must be a positive integer');
      }

      const result = await updateVoteboardId(
        collection.creatorAddress,
        collection.taxon,
        newId
      );
      setVoteboardSuccess(result.message);
      setIsEditingVoteboardId(false);
      // Clear success message after 5 seconds and refresh
      setTimeout(() => {
        setVoteboardSuccess(null);
        onMethodologyUpdated?.(); // Refresh the collection data
      }, 3000);
    } catch (error: any) {
      setVoteboardError(error.message || 'Failed to update VoteBoard ID');
    } finally {
      setIsUpdatingVoteboardId(false);
    }
  };

  // Handle rugged status toggle
  const handleRuggedToggle = async () => {
    const newRuggedState = !collection.ruggedAt;

    setIsUpdatingRugged(true);
    setRuggedError(null);
    setRuggedSuccess(null);

    try {
      const result = await updateRuggedStatus(
        collection.creatorAddress,
        collection.taxon,
        newRuggedState,
        true // trigger valuation recalculation
      );
      setRuggedSuccess(result.message);
      // Clear success message after 5 seconds and refresh
      setTimeout(() => {
        setRuggedSuccess(null);
        onValuationStrategyUpdated?.(); // Refresh the collection data
      }, 3000);
    } catch (error: any) {
      setRuggedError(error.message || 'Failed to update rugged status');
    } finally {
      setIsUpdatingRugged(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-start gap-6 mb-6">
        {/* Collection Image */}
        {collection.imageUrl && (
          <img
            src={collection.imageUrl}
            alt={collection.name}
            className="w-24 h-24 rounded-lg object-cover"
          />
        )}

        {/* Collection Metadata */}
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{collection.name}</h2>
          {collection.description && (
            <p className="text-gray-600 mb-2">{collection.description}</p>
          )}
          <div className="flex gap-4 text-sm text-gray-500">
            <div>
              <span className="font-medium">Creator:</span>{' '}
              <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                {collection.creatorAddress}
              </code>
            </div>
            <div>
              <span className="font-medium">Taxon:</span>{' '}
              {collection.taxon !== null ? collection.taxon : <span className="text-purple-600 font-medium">Issuer-Wide</span>}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Floor Price */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">Floor Price</div>
          <div className="text-xl font-bold text-gray-900">
            {formatXRP(collection.floorPrice)}
          </div>
        </div>

        {/* 24h Volume */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">24h Volume</div>
          <div className="text-xl font-bold text-gray-900">
            {formatXRP(collection.volume24h)}
          </div>
        </div>

        {/* 7d Volume */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">7d Volume</div>
          <div className="text-xl font-bold text-gray-900">
            {formatXRP(collection.volume7d)}
          </div>
        </div>

        {/* 30d Volume */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">30d Volume</div>
          <div className="text-xl font-bold text-gray-900">
            {formatXRP(collection.volume30d)}
          </div>
        </div>

        {/* All Time Volume */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">All Time Volume</div>
          <div className="text-xl font-bold text-gray-900">
            {formatXRP(collection.volumeAllTime)}
          </div>
        </div>

        {/* Total Supply */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm text-gray-500 mb-1">Total Supply</div>
          <div className="text-xl font-bold text-gray-900">
            {collection.totalSupply.toLocaleString()}
          </div>
        </div>

        {/* Collection ID */}
        <div className="bg-gray-50 p-4 rounded-lg col-span-2">
          <div className="text-sm text-gray-500 mb-1">Collection ID</div>
          <code className="text-xs text-gray-900 break-all">{collection.id}</code>
        </div>
      </div>

      {/* NFT Processing Status Breakdown */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">NFT Processing Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Pending */}
          <div className={`p-3 rounded-lg ${getStatusColor('pending')}`}>
            <div className="text-xs font-medium mb-1">{formatStatusLabel('pending')}</div>
            <div className="text-2xl font-bold">{nftStats.byStatus.pending.toLocaleString()}</div>
          </div>

          {/* Metadata Fetched */}
          <div className={`p-3 rounded-lg ${getStatusColor('metadata_fetched')}`}>
            <div className="text-xs font-medium mb-1">{formatStatusLabel('metadata_fetched')}</div>
            <div className="text-2xl font-bold">{nftStats.byStatus.metadata_fetched.toLocaleString()}</div>
          </div>

          {/* Processing Media */}
          <div className={`p-3 rounded-lg ${getStatusColor('processing_media')}`}>
            <div className="text-xs font-medium mb-1">{formatStatusLabel('processing_media')}</div>
            <div className="text-2xl font-bold">{nftStats.byStatus.processing_media.toLocaleString()}</div>
          </div>

          {/* Completed */}
          <div className={`p-3 rounded-lg ${getStatusColor('completed')}`}>
            <div className="text-xs font-medium mb-1">{formatStatusLabel('completed')}</div>
            <div className="text-2xl font-bold">{nftStats.byStatus.completed.toLocaleString()}</div>
          </div>

          {/* Failed */}
          <div className={`p-3 rounded-lg ${getStatusColor('failed')}`}>
            <div className="text-xs font-medium mb-1">{formatStatusLabel('failed')}</div>
            <div className="text-2xl font-bold">{nftStats.byStatus.failed.toLocaleString()}</div>
          </div>
        </div>
        <div className="mt-2 text-sm text-gray-500">
          Total NFTs: <span className="font-medium">{nftStats.total.toLocaleString()}</span>
        </div>
      </div>

      {/* Rarity Methodology Section */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Rarity Calculation</h3>
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-gray-500 mb-1">Current Methodology</div>
            <div className="flex items-center gap-3">
              <RarityMethodologySelect
                value={collection.rarityMethodology}
                onChange={handleMethodologyChange}
                disabled={isUpdatingMethodology}
              />
              {isUpdatingMethodology && (
                <span className="text-sm text-blue-600">Updating...</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {getMethodologyDescription(collection.rarityMethodology)}
            </p>
          </div>
        </div>
        {methodologyError && (
          <div className="mt-2 text-sm text-red-600">{methodologyError}</div>
        )}
      </div>

      {/* Confirmation Dialog for Rarity Methodology */}
      {showConfirmDialog && pendingMethodology && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelMethodologyChange} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Change Rarity Methodology?
            </h3>
            <p className="text-gray-600 mb-4">
              Changing from <strong>{getMethodologyLabel(collection.rarityMethodology)}</strong> to{' '}
              <strong>{getMethodologyLabel(pendingMethodology)}</strong> will trigger a full
              recalculation of rarity scores for all {nftStats.total.toLocaleString()} NFTs in
              this collection.
            </p>
            {pendingMethodology === 'NONE' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Setting methodology to "No Rarity" will set all
                  rarity scores and ranks to NULL. NFTs will no longer display rarity information.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelMethodologyChange}
                disabled={isUpdatingMethodology}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmMethodologyChange}
                disabled={isUpdatingMethodology}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isUpdatingMethodology ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Valuation Strategy Section */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">NFT Valuation</h3>

        {/* Strategy Guide */}
        <div className="mb-4 text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
          <div className="font-medium text-gray-700 mb-2">Strategy Guide:</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div><strong>Default:</strong> Full model with traits, liquidity, trends</div>
            <div><strong>Floor Only:</strong> Simple floor × rank adjustment (small collections)</div>
            <div><strong>Percentile Rank:</strong> Handles tied/duplicate rarity ranks</div>
            <div><strong>Sales History:</strong> 95% weight on individual NFT sales</div>
            <div><strong>Conservative:</strong> Default capped at 2× floor (volatile markets)</div>
            <div><strong>Trait Demand:</strong> Uses trait premiums, ignores rarity</div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex-1">
            <div className="text-sm text-gray-500 mb-1">Current Strategy</div>
            <div className="flex items-center gap-3">
              <ValuationStrategySelect
                value={collection.valuationStrategy ?? 'DEFAULT'}
                onChange={handleStrategyChange}
                disabled={isUpdatingStrategy}
              />
              {isUpdatingStrategy && (
                <span className="text-sm text-blue-600">Updating...</span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {getStrategyDescription(collection.valuationStrategy ?? 'DEFAULT')}
            </p>
          </div>
        </div>
        {strategyError && (
          <div className="mt-2 text-sm text-red-600">{strategyError}</div>
        )}

        {/* Recalculate Valuations Button */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <button
              onClick={handleTriggerValuation}
              disabled={isTriggeringValuation || collection.valuationStrategy === 'NONE'}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTriggeringValuation ? 'Triggering...' : 'Recalculate Valuations Now'}
            </button>
            <span className="text-xs text-gray-500">
              Trigger immediate recalculation without changing strategy
            </span>
          </div>
          {valuationTriggerSuccess && (
            <div className="mt-2 text-sm text-green-600">{valuationTriggerSuccess}</div>
          )}
          {valuationTriggerError && (
            <div className="mt-2 text-sm text-red-600">{valuationTriggerError}</div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog for Valuation Strategy */}
      {showStrategyConfirmDialog && pendingStrategy && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={cancelStrategyChange} />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-white shadow-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Change Valuation Strategy?
            </h3>
            <p className="text-gray-600 mb-4">
              Changing from <strong>{getStrategyLabel(collection.valuationStrategy ?? 'DEFAULT')}</strong> to{' '}
              <strong>{getStrategyLabel(pendingStrategy)}</strong> will affect how NFT estimated
              values are calculated.
            </p>
            {pendingStrategy === 'NONE' && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> Setting strategy to "No Valuation" will set all
                  estimated values to NULL. NFTs will no longer display valuation estimates.
                </p>
              </div>
            )}
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Valuations will be recalculated within the next minute.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelStrategyChange}
                disabled={isUpdatingStrategy}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmStrategyChange}
                disabled={isUpdatingStrategy}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isUpdatingStrategy ? 'Updating...' : 'Confirm Change'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* XRPL VoteBoard Section */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">XRPL VoteBoard</h3>
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">VoteBoard ID</div>
              {isEditingVoteboardId ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={voteboardIdInput}
                    onChange={(e) => setVoteboardIdInput(e.target.value)}
                    placeholder="Enter ID (e.g., 42)"
                    className="w-32 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    disabled={isUpdatingVoteboardId}
                    min={1}
                  />
                  <button
                    onClick={handleSaveVoteboardId}
                    disabled={isUpdatingVoteboardId}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {isUpdatingVoteboardId ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancelVoteboardEdit}
                    disabled={isUpdatingVoteboardId}
                    className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-lg font-medium text-gray-900">
                    {collection.xrplVoteboardId ?? 'Not set'}
                  </span>
                  <button
                    onClick={handleEditVoteboardId}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-500 mb-1">Current Score</div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-lg font-medium text-gray-900">
                  {collection.xrplVoteboardScore != null
                    ? collection.xrplVoteboardScore.toFixed(2)
                    : 'N/A'}
                </span>
                {collection.xrplVoteboardData?.trustTier && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold"
                    style={{
                      borderColor: collection.xrplVoteboardData.trustTier.color,
                      color: collection.xrplVoteboardData.trustTier.color,
                      backgroundColor: `${collection.xrplVoteboardData.trustTier.color}1A`,
                    }}
                  >
                    {collection.xrplVoteboardData.trustTier.tier.charAt(0).toUpperCase() + collection.xrplVoteboardData.trustTier.tier.slice(1)}
                  </span>
                )}
                {collection.xrplVoteboardData?.currentPosition != null && (
                  <span className="text-sm text-gray-500">
                    Rank #{collection.xrplVoteboardData.currentPosition}
                  </span>
                )}
              </div>
              {collection.xrplVoteboardScore != null && (
                <a
                  href={collection.xrplVoteboardData?.slug
                    ? `https://nft.xrplvoteboard.com/collection/${collection.xrplVoteboardData.slug}`
                    : 'https://nft.xrplvoteboard.com/'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  View on VoteBoard →
                </a>
              )}
            </div>
          </div>
          {collection.xrplVoteboardData?.slug && (
            <div className="mt-2 text-xs text-gray-500">
              VB Slug: <code className="bg-gray-100 px-1 py-0.5 rounded">{collection.xrplVoteboardData.slug}</code>
            </div>
          )}
          {voteboardError && (
            <div className="mt-2 text-sm text-red-600">{voteboardError}</div>
          )}
          {voteboardSuccess && (
            <div className="mt-2 text-sm text-green-600">{voteboardSuccess}</div>
          )}
          <p className="mt-3 text-xs text-gray-500">
            The VoteBoard ID maps this collection to the{' '}
            <a href="https://nft.xrplvoteboard.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
              XRPL VoteBoard
            </a>{' '}
            for community voting scores. Scores are synced every 5 minutes. Collections may also be auto-matched by name.
          </p>
        </div>
      </div>

      {/* Rugged Status Section */}
      <div className="mt-6 border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Collection Status</h3>
        <div className={`p-4 rounded-lg ${collection.ruggedAt ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!collection.ruggedAt}
                onChange={handleRuggedToggle}
                disabled={isUpdatingRugged}
                className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:opacity-50"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">
                  Mark as Rugged
                </span>
                {collection.ruggedAt && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                    RUGGED
                  </span>
                )}
              </div>
            </label>
            {isUpdatingRugged && (
              <span className="text-sm text-blue-600">Updating...</span>
            )}
          </div>
          {collection.ruggedAt && (
            <p className="mt-2 text-sm text-red-700">
              Rugged on: {new Date(collection.ruggedAt).toLocaleString()}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            When marked as rugged, NFT valuations for this collection are automatically reduced by 75%.
            This helps reflect the diminished value of collections where the project has been abandoned or compromised.
          </p>
          {ruggedError && (
            <div className="mt-2 text-sm text-red-600">{ruggedError}</div>
          )}
          {ruggedSuccess && (
            <div className="mt-2 text-sm text-green-600">{ruggedSuccess}</div>
          )}
        </div>
      </div>

      {/* Last Stats Refresh */}
      <div className="mt-4 text-sm text-gray-500">
        <span className="font-medium">Last Stats Refresh:</span>{' '}
        {formatDate(collection.lastStatsRefreshAt)}
      </div>
    </div>
  );
}
